import { GridApi, GridReadyEvent } from '@amzn/gft-ag-grid-enterprise';
import type { GridManager } from '@deckManagement/hooks/usePageGridManager';
import { MutableRefObject, useCallback } from 'react';

/**
 * Provides centralized grid management functionality that can be used across components.
 */
export interface GridRefreshOptions {
  /** Force refresh even if data hasn't changed */
  force?: boolean;
  /** Skip header refresh */
  skipHeader?: boolean;
  /** Skip row redraw */
  skipRedraw?: boolean;
}

/**
 * Enhanced custom hook to handle AG Grid Ready event and provide grid utilities
 * @param gridApiRef - Mutable reference to the AG Grid API
 * @param gridOptions - Optional configuration for grid ready behavior
 * @returns Object containing grid ready callback and utility functions
 */
const useGridReady = (
  gridApiRef: MutableRefObject<GridApi | null>,
  gridOptions?: {
    gridManager?: GridManager;
    sectionLookupKey?: string;
  },
) => {
  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      // Allow the assignment since we deliberately intend the passed in Ref to be updated here.
      // eslint-disable-next-line no-param-reassign
      gridApiRef.current = params.api;

      // Register grid with manager if provided
      if (gridOptions?.gridManager && gridOptions?.sectionLookupKey) {
        gridOptions.gridManager.registerPageGrid(gridOptions.sectionLookupKey || 'default', params.api);
      }
    },
    [gridApiRef, gridOptions],
  );

  /**
   * Comprehensive grid refresh function
   * Forces cell refresh, row redraw, and header refresh for complete UI sync
   */
  const refreshGrid = useCallback(
    (options: GridRefreshOptions = {}) => {
      if (!gridApiRef.current) {
        return;
      }

      const { force = true, skipHeader = false, skipRedraw = false } = options;

      try {
        const refreshParams = { force };

        gridApiRef.current.refreshCells(refreshParams);

        if (!skipRedraw) {
          gridApiRef.current.redrawRows();
        }

        if (!skipHeader) {
          gridApiRef.current.refreshHeader();
        }
      } catch (error) {
        console.error('Error refreshing grid:', error);
      }
    },
    [gridApiRef],
  );

  /**
   * Specialized refresh functions for common scenarios
   */
  const refreshForHighlighting = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }

    try {
      gridApiRef.current.redrawRows();
      gridApiRef.current.refreshCells({ force: true });
    } catch (error) {
      console.error('Error refreshing grid for highlighting:', error);
    }
  }, [gridApiRef]);

  /**
   * Auto-size expandable columns based on content
   */
  const autoSizeExpandableColumns = useCallback(
    (gridApi?: GridApi) => {
      const api = gridApi || gridApiRef.current;
      if (!api) return;

      const expandableCols = api.getColumns()?.filter((col) => col.getColId().startsWith('dimension-'));
      if (expandableCols?.length) {
        api.autoSizeColumns(expandableCols.map((col) => col.getColId()));
      }
    },
    [gridApiRef],
  );

  return {
    onGridReady,
    refreshGrid,
    refreshForHighlighting,
    autoSizeExpandableColumns,
  };
};

export default useGridReady;
